<template>
  <div>
    <TopSection
      :title="$t('soundSysDatabase') "
      :count="displayCount"
    >
      <template v-slot:filters>
        <FilterModule
          :title="$t('filterSoundRecordings')|capitalize"
          :statuses="{
            [statusTypes.CLAIMED]: $t('claimed'),
            [statusTypes.UNCLAIMED]: $t('unclaimed'),
            [statusTypes.OVERCLAIMED]: $t('overclaimed'),
            [statusTypes.UNDERCLAIMED]: $t('underclaimed')
          }"
          :table="$refs.SoundRecordingsTable"
          :use-types-list="rights"
          :members-list="membersList"
        />
      </template>
    </TopSection>

    <div class="d-flex align-items-center">
      <div
        class="link-class"
        @click="toggleSelectAll"
      >
        {{ $t('un_select_all') | capitalize }}
      </div>
      <b-button
        v-b-modal="'claimConfirmationModal'"
        size="sm"
        class="ml-4"
        variant="primary"
        @click="addToClaimList"
      >
        {{ $t('addSelectedToClaimList') | capitalize }}
      </b-button>
      <SelectedFilters class="ml-4" :table="$refs.SoundRecordingsTable"/>
    </div>

    <!-- TABLE -->
    <v-server-table
      ref="SoundRecordingsTable"
      :url="url"
      :columns="columns"
      :options="options"
      @loaded="getCount"
      @pagination="onPagination"
      @filter="onFilter"
    >
      <!-- TITLE COLUMN -->
      <template v-slot:title="{ row }">
        <div class="d-flex align-items-center">
          <div>
            <p-check
              :checked="selectedSoundRecordings[row.id]"
              class="p-icon p-curve p-pulse"
              color="success"
              @change="toggleSoundRecording(row.id)"
            >
              <template v-slot:extra>
                <i class="icon fa fa-check"/>
              </template>
            </p-check>
          </div>
          <div>
            <div
              class="clickable table-header"
              @click="navigateToSR(row.id)"
            >
              {{ row.title | capitalize }}
            </div>
            <div class="mb-1">
              <div
                v-for="(metadata, idx) in getMetadata(row,'title')"
                :key="idx"
                class="table-meta mb-2"
              >
                {{ metadata }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ARTIST COLUMN -->
      <template v-slot:artist="{ row }">
        <div class="table-header">
          {{ row.artist | capitalize }}
        </div>
        <div class="mb-1">
          <div
            v-for="(metadata, idx) in getMetadata(row,'artist')"
            :key="idx"
            class="table-meta mb-2"
          >
            {{ metadata }}
          </div>
        </div>
      </template>

      <!-- ISRC COLUMN -->
      <template v-slot:isrc="{ row }">
        <div class="table-main-class">
          {{ row.isrc || '-' | upper }}   
        </div>
      </template>

      <!-- DURATION COLUMN-->
      <template v-slot:duration="{ row }">
        {{ row.duration | normalizeSeconds }}
      </template>

      <!-- COUNTRIES COLUMN-->
      <template v-slot:fixation_country="{ row }">
        {{ row.fixation_country | get("name") }}
      </template>

      <!-- LANGUAGE OF PERF COLUMN-->
      <template v-slot:lang_of_perf="{ row }">
        {{ getMetadata(row,'lang_of_perf')[0] }}
      </template>

      <!-- RIGHT OWNERS COLUMN-->
      <template v-slot:rightOwners="{ row }">
        {{ row.right_owners.join(", ") }}
      </template>

      <!-- RELEASE NAMES COLUMN-->
      <template v-slot:releaseNames="{ row }">
        <ReleaseNames
          class-names="table-meta mb-2"
          :releases="getMetadata(row,'release')"
        />
      </template>

      <!-- STATUS COLUMN-->
      <template v-slot:status="{ row }">
        <Status
          v-if="row.status == statusTypes.CLAIMED"
          :text="$t('claimed') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.UNCLAIMED"
          :text="$t('unclaimed') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.UNDERCLAIMED"
          :text="$t('underclaimed') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.OVERCLAIMED"
          :text="$t('overclaimed') | capitalize"
          :icon="['fas', 'exclamation-circle']"
          color="red"
        />
      </template>

      <!-- ELIGIBILITY COLUMN-->
      <template v-slot:eligible="{ row }">
        <i :class="row.eligible ? 'fas fa-check-circle active_state' : 'fas fa-times-circle blocked_state'" />
      </template>

      <!-- ACTIONS COLUMN -->
      <template v-slot:actions="{ row }">
        <div class="flex items-center justify-end pr-4">
          <div
            class="clickable table-sub-class"
            @click="navigateToSR(row.id)"
          >
            <i class="fas fa-eye text-sm" />
          </div>
        </div>
      </template>
    </v-server-table>

  </div>
</template>


<script>
import { capitalize, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapGetters, mapState } from "vuex"
import { listRouteMixin } from "@/utils/mixins"

export default {
  name: "SoundRecordingsTable",
  mixins: [listRouteMixin],

  data () {
    return {
      url: "soundrecordings/",
      addedSrs: 0,
      count:0,
      displayCount:0,
      claimsModalOpen: false,
      selectedSoundRecordings: {},
      allMarked: false,
      queryStrings: {},
      options: {
        previousPage: null,
        sortable: [],
        filterable: true,
        headings: {
          title: capitalize(this.$t("title")),
          artist: capitalize(this.$t("artist")),
          duration: capitalize(this.$t("duration")),
          isrc: capitalize(this.$t("isrc")),
          type: capitalize(this.$t("type")),
          lang_of_perf: capitalize(this.$t("lang_of_perf")),
          fixation_country: capitalize(this.$t("fixation_country")),
          releaseNames: capitalize(this.$t("releaseNames")),
          fixation_year: capitalize(this.$t("fixation_year")),
          rightOwners: capitalize(this.$t("rightOwners")),
          status: capitalize(this.$t("claimStatus")),
          eligible: capitalize(this.$t("isEligible")),
          actions: ""
        },
        columnsClasses: {
          artist: "width-large",
        },
        requestFunction (params) {
          const queryParams = { ...params, ...this.$route.query }
          this.query = get(queryParams, "fuzzy_search", "")
          this.page = Number(get(queryParams, "page", 1))
          if (this.page > 1000) this.page = 1
          // Set relevant variables in the SoundRecordingTable
          this.$parent.$parent.queryStrings = queryParams

          return doRequest(this.url, queryParams, this.$toasted.global.toast)
        },
        responseAdapter: resp => {
          const response = responsePaginatedAdapter(resp)
          this.count = Math.min(response.count, 10000)
          this.displayCount = response.count
          return response
        }
      }
    }
  },
  methods: {
    addToClaimList () {
      let currentClaimList = localStorage.getItem("claimList")
      if (currentClaimList) {
        currentClaimList = JSON.parse(currentClaimList)
      } else {
        currentClaimList = []
      }

      const selectedSoundRecordings = this.$refs.SoundRecordingsTable.data.reduce(
        (current, next) => {
          const currentItem = currentClaimList.find(
            item => item.id === next.id
          )
          if (this.selectedSoundRecordings[next.id] && !currentItem) {
            current.push(next)
          }
          return current
        },
        []
      )
      this.addedSrs = selectedSoundRecordings.length
      const updatedClaimList = [
        ...currentClaimList,
        ...selectedSoundRecordings
      ]
      localStorage.setItem("claimList", JSON.stringify(updatedClaimList))
      let message = this.addedSrs ? this.$t("addedToClaimList") : this.$t("nothingToAdd")
      this.$toasted.global.toast(message)
    },
    getCount () {
      if (this.options.previousPage !== this.$route.query.page) {
        this.$refs.SoundRecordingsTable.$refs.table.count = this.count
        return
      }
      this.$refs.SoundRecordingsTable.$refs.table.count = this.count
      this.options.previousPage = this.$route.query.page
    },
    getData () {
      this.$refs.SoundRecordingsTable.getData()
    },
    getMetadata ({ extras }, tag) {
      if (extras) {
        return extras.reduce((current, next) => {
          if (next.tag === tag) {
            current.push(next.value)
          }
          return current
        }, [])
      }
      return []
    },
    toggleSelectAll () {
      this.allMarked = !this.allMarked
      if (!this.allMarked) {
        this.selectedSoundRecordings = {}
      } else {
        const selectedSoundRecordings = {}
        this.$refs.SoundRecordingsTable.data.forEach(item => {
          selectedSoundRecordings[item.id] = true
        })
        this.selectedSoundRecordings = selectedSoundRecordings
      }
    },
    navigateToSR (id) {
      this.$router.push({
        name: "sound-recording-from-db-detail",
        params: {
          id
        }
      })
    },
    toggleSoundRecording (id) {
      this.selectedSoundRecordings[id] = !this.selectedSoundRecordings[id]
      this.selectedSoundRecordings = { ...this.selectedSoundRecordings }
    },
    onFilter (search) {
      this.options.previousPage = null
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
  },
  computed: {
    ...mapGetters("soundsys", ["isMlc", "srTypes", "membersList", "statusTypes"]),
    ...mapState("soundsys", ["rights"]),
    ...mapState("ui", ["statusFilter", "useTypeFilter", "memberFilter", "showIneligible"]),
    columns ()  {
      return [ 
        "title",
        "artist",
        "isrc",
        "duration",
        "type",
        "fixation_country",
        "lang_of_perf",
        "fixation_year",
        "releaseNames",
        "rightOwners",
        "status",
        this.showIneligible ? "eligible" : "",
        "actions"
      ]
    },
  }
}
</script>
